import Button from '../../../common/Button';
import FlexContainer from '../../../common/FlexContainer';
import {
	formFieldHasErrors,
	FormResults,
	genErrorIdFromLabel,
} from '../../../common/Form';
import Spinner from '../../../common/Spinner';
import Typography from '../../../common/Typography';
import { useOperateAttrMutation } from '../../api';
import { mergeErrorStates, extractQueryErrMessage } from '../../api/helpers';
import { BaseAttribute } from '../../ontology/types/attributeTypes';
import { AttrActionFormProps } from '../common/commonTypes';
import {
	renderReferenceField,
	renderDerivationSubfields,
} from '../common/jsxHelpers';
import { StyledFlexContainer, StyledPaper } from '../common/styledComponents';
import useAttrNeighbors from '../hooks/useAttrNeighbors';
import {
	prependAttrFormDefaults,
	prependAttrFormToPayload,
} from './prependAttributeHelpers';
import { PrependAttrFormValues } from './prependAttributeTypes';
import { FunctionComponent, useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

const PrependAttributeForm: FunctionComponent<AttrActionFormProps> = (
	props
) => {
	const { _id, plural: label, updatePopper } = props;

	const formDefaults = prependAttrFormDefaults();

	const { handleSubmit, register, formState, watch } =
		useForm<PrependAttrFormValues>({
			defaultValues: formDefaults,
		});

	const [watchedDerivationFlag, watchedUsingReferenceValue] = watch([
		'isDerivation',
		'usingStaticReference',
	]);

	// prevent filtering from running every render
	const filter = useCallback(
		(attr: BaseAttribute) => attr.type === 'category',
		[]
	);

	const queryRes = useAttrNeighbors(_id, filter);

	useEffect(() => {
		if (queryRes.isSuccess) {
			updatePopper();
		}
	}, [queryRes.isSuccess, updatePopper]);

	const [prependAttr, prependAttrResults] = useOperateAttrMutation();

	const mergedErrs = mergeErrorStates(queryRes, prependAttrResults);

	const onSubmit: SubmitHandler<PrependAttrFormValues> = (vals, e) => {
		e?.preventDefault();
		prependAttr({
			attrId: _id,
			body: prependAttrFormToPayload(vals),
		});
	};

	if (queryRes.isLoading) {
		return (
			<FlexContainer justifyContent="center">
				<Typography paragraph>Loading attributes...</Typography>
				<Spinner />
			</FlexContainer>
		);
	}

	if (queryRes.isError) {
		return (
			<FlexContainer justifyContent="center">
				<Typography color="error" paragraph>
					{extractQueryErrMessage(queryRes.error)}
				</Typography>
			</FlexContainer>
		);
	}

	return (
		<StyledPaper>
			<form onSubmit={handleSubmit(onSubmit)}>
				<StyledFlexContainer flexDirection="column" alignItems="center">
					<Typography id="isDerivation">
						Transformation will replace {label}, derivation will
						create a new attribute
					</Typography>
					<div
						role="radiogroup"
						aria-labelledby="isDerivation"
						aria-errormessage={genErrorIdFromLabel('isDerivation')}
						aria-invalid={formFieldHasErrors(
							'isDerivation',
							formState
						)}
					>
						<div>
							<input
								id="isDerivation-true"
								type="radio"
								value="true"
								{...register('isDerivation', {
									required: {
										value: true,
										message:
											'A value for isDerivation is required',
									},
								})}
							/>
							<label htmlFor="isDerivation-true">
								<Typography>Use derivation</Typography>
							</label>
						</div>
						<div>
							<input
								id="isDerivation-false"
								type="radio"
								value="false"
								{...register('isDerivation', {
									required: {
										value: true,
										message:
											'A value for isDerivation is required',
									},
								})}
							/>
							<label htmlFor="isDerivation-false">
								<Typography>Use transformation</Typography>
							</label>
						</div>
					</div>

					{renderDerivationSubfields(
						watchedDerivationFlag === 'true',
						register,
						formState
					)}

					<Typography id="usingStaticReference">
						Use another attribute or set a static value to prepend
						to {label}
					</Typography>
					<div
						role="radiogroup"
						aria-errormessage={genErrorIdFromLabel(
							'usingStaticReference'
						)}
						aria-invalid={formFieldHasErrors(
							'usingStaticReference',
							formState
						)}
						aria-labelledby="usingStaticReference"
					>
						<div>
							<input
								id="dv-false"
								type="radio"
								value="false"
								{...register('usingStaticReference', {
									required: {
										value: true,
										message:
											'A value for usingStaticReference is required',
									},
								})}
							/>
							<label htmlFor="dv-false">
								<Typography>Select an attribute</Typography>
							</label>
						</div>
						<div>
							<input
								id="dv-true"
								type="radio"
								value="true"
								{...register('usingStaticReference', {
									required: {
										value: true,
										message:
											'A value for usingStaticReference is required',
									},
								})}
							/>
							<label htmlFor="dv-true">
								<Typography>Set a static value</Typography>
							</label>
						</div>
					</div>

					{renderReferenceField(
						'otherId',
						'otherValue',
						watchedUsingReferenceValue === 'true',
						register,
						queryRes.attrNeighbors,
						formState
					)}

					<label htmlFor="delimiter">
						<Typography>
							Choose a delimiter for the joined values
						</Typography>
					</label>
					<select
						{...register('delimiter', {
							required: true,
						})}
						aria-errormessage={genErrorIdFromLabel('delimiter')}
						aria-invalid={formFieldHasErrors(
							'delimiter',
							formState
						)}
					>
						<option value="none">None</option>
						<option value="space">Space: ( )</option>
						<option value="comma">Comma: ,</option>
						<option value="pipe">Pipe: |</option>
						<option value="colon">Colon: :</option>
					</select>

					<Button type="submit">Submit</Button>

					<FormResults
						isError={!!mergedErrs}
						error={mergedErrs}
						validationErrors={formState.errors}
					/>
				</StyledFlexContainer>
			</form>
		</StyledPaper>
	);
};

export default PrependAttributeForm;
